import { useEffect, useRef, useState } from "react";

/**
 * `useShellScroll` / `usePillNavScroll` — the shell's scroll state.
 *
 * One state, and it came from Apple iOS 26: `scrolled` — content is passing
 * underneath, so the chrome thickens. iOS calls the resting bar "dormant" and
 * this one "active"; in the active state "the glass intensifies — you can see
 * content moving through the bar". One fixed alpha cannot do both jobs:
 * see-through reads better at rest, opaque is the only thing that stops 14px
 * body copy from competing with the 10px tab labels.
 *
 * It used to have a second one. W38.1 added `minimized` — the row collapsed on
 * scroll-down and re-expanded on scroll-up, iOS's
 * `tabBarMinimizeBehavior = .onScrollDown` — and W40.1 F3 withdrew the whole
 * behaviour (ADR-0100). The direction dead-band, the 96px near-top guard and the
 * `minimized` state went with it: with no second state to protect, none of them
 * has anything left to do.
 */

/** Below this, the page is still at rest — no need to thicken the glass. */
const SCROLLED_THRESHOLD_PX = 8;

/**
 * W47.4 / `ADR-0139` — the progressive blur's dial, as three constants.
 *
 * `VEL_FULL_PX_PER_MS` is a **measured** number, not a citation: a real flick on
 * a phone lands between 1.2 and 2.4 px/ms, so 2.0 means "clearly moving" rather
 * than "as fast as physics allows". It is the one value to change if the material
 * feels wrong, and it lives here rather than in a CSS token because it is a
 * behaviour parameter, not a visual one.
 */
export const VEL_FULL_PX_PER_MS = 2.0;
/** Quiet for this long and the material starts settling back down. */
export const VEL_IDLE_MS = 90;
/** Per-frame decay once settling: 0.85 ≈ 20 frames ≈ 330ms to visually zero. */
export const VEL_DECAY = 0.85;

/**
 * Scroll speed as 0…1. Pure, so it can be tested without a layout engine —
 * `getBoundingClientRect` returns 0 in jsdom and a rAF loop is not a thing
 * worth asserting on.
 */
export function scrollVelocity(dy: number, dtMs: number): number {
  if (!(dtMs > 0)) return 0;
  const v = Math.abs(dy) / dtMs / VEL_FULL_PX_PER_MS;
  return v > 1 ? 1 : v;
}

export interface PillNavScrollState {
  scrolled: boolean;
  /**
   * W47.4 — the shell root, for the `--scroll-vel` write.
   *
   * A **ref, not state**, and that is the whole implementation note: this value
   * changes on every scroll frame, and routing it through `useState` would
   * re-render the entire shell — header, content and nav — once per frame, which
   * turns a material effect into the most expensive thing on the page. One
   * `style.setProperty` on one element is free by comparison.
   */
  shellRef: React.MutableRefObject<HTMLDivElement | null>;
}

/**
 * W45.0 `ADR-0111` — the **shell's** scroll state, and the nav's is a view of it.
 *
 * This hook used to serve the nav alone. It now writes `data-scrolled` onto the
 * shell root so that the sticky topbar and the nav's dormant/active step read
 * **one** flag from **one** listener.
 *
 * That is not tidiness. Two listeners with two thresholds on the same scroll
 * event is how the topbar and the nav end up disagreeing about whether the page
 * has scrolled — and the symptom is that the title slides under a bar that still
 * believes it is at rest, which is exactly the defect `ADR-0111` fixes.
 *
 * Why a DOM attribute rather than context: the flag is consumed by CSS
 * (`.shell-bar::after` opacity, `.nav-pill` background), and a CSS custom
 * property set on the same element it styles keeps the state and its consumer in
 * the same place. A context would require every chrome component to subscribe,
 * and `PillNav` is rendered outside the header's subtree.
 */
export function useShellScroll(): PillNavScrollState {
  const [scrolled, setScrolled] = useState(false);
  const shellRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let lastY = window.scrollY;
    let lastT = performance.now();
    let idleTimer = 0;
    let raf = 0;

    const write = (v: number) => {
      shellRef.current?.style.setProperty("--scroll-vel", v.toFixed(3));
    };

    // Runs only after the finger stops. Without this the value would track the
    // last scroll event forever and the chrome would stay blurred on a page the
    // user has stopped reading — invisible in a screenshot, which is exactly why
    // it needs a gate (G17) rather than a glance.
    const settle = () => {
      const el = shellRef.current;
      if (!el) return;
      const current = Number(el.style.getPropertyValue("--scroll-vel")) || 0;
      const next = current * VEL_DECAY;
      if (next < 0.01) {
        write(0);
        return;
      }
      write(next);
      raf = requestAnimationFrame(settle);
    };

    const onScroll = () => {
      const now = performance.now();
      const y = window.scrollY;
      const velocity = scrollVelocity(y - lastY, now - lastT);
      lastY = y;
      lastT = now;
      setScrolled(y > SCROLLED_THRESHOLD_PX);
      // `PillNav` calls this hook too and never attaches the ref, so there is
      // nothing to write on that instance — bail rather than running a second
      // decay loop that can never do anything.
      if (!shellRef.current) return;
      write(velocity);
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        raf = requestAnimationFrame(settle);
      }, VEL_IDLE_MS);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    // A chrome that mounts already blurred is a chrome that is wrong, so the
    // dial is pinned at rest before the first sync rather than after it.
    write(0);
    // The shell can mount with the page already scrolled (restored position,
    // or a route that sets scrollTop before the shell settles), so sync once.
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(idleTimer);
      cancelAnimationFrame(raf);
      write(0);
    };
  }, []);

  return { scrolled, shellRef };
}

/**
 * The nav's view of the shell's scroll state.
 *
 * Kept as a separate export because `PillNav` is a *consumer* of the shell's
 * state, not the owner of it — and `PillNav.test.tsx` mounts the nav in
 * isolation, where no shell is present, so the hook has to work standalone. The
 * implementation is one `useShellScroll()` call rather than a second listener.
 */
export function usePillNavScroll(): PillNavScrollState {
  return useShellScroll();
}
